// @file vue composable for handling reduced motion mode
import { AccessibilitySettingPreference } from '@@/enums'
import type { User, UserCamelCase } from '@@/types'
import { useAccessibilitySettings } from '@@/vuecomposables/useAccessibilitySettings'
import { createSharedComposable, usePreferredReducedMotion } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

const useReducedMotionMode = createSharedComposable((user?: Ref<User | UserCamelCase | null | undefined>) => {
  const { reducedMotionPreference: userPreferredMotion } = useAccessibilitySettings(user)
  const systemPreferredMotion = usePreferredReducedMotion()
  const customPreferredMotion = ref<AccessibilitySettingPreference | null>(null)

  /**
   * Computed value that determines if reduced motion mode should be enabled
   * Checks in order of precedence:
   * 1. Page-specific override (if set)
   * 2. User preference (if set)
   * 3. System preference
   */
  const isInReducedMotionMode = computed(() => {
    if (customPreferredMotion.value != null) {
      if (customPreferredMotion.value === AccessibilitySettingPreference.System) {
        return systemPreferredMotion.value === 'reduce'
      }
      return customPreferredMotion.value === AccessibilitySettingPreference.On
    }

    // User preference takes precedence when set and not 'system'
    if (userPreferredMotion.value != null && userPreferredMotion.value !== AccessibilitySettingPreference.System) {
      return userPreferredMotion.value === AccessibilitySettingPreference.On
    }

    // Fall back to system preference
    return systemPreferredMotion.value === 'reduce'
  })

  const setCustomPreferredMotion = (motion: AccessibilitySettingPreference): void => {
    customPreferredMotion.value = motion
  }

  watch(
    isInReducedMotionMode,
    (isReducedMotionMode) => {
      document.documentElement.dataset.reducedMotionMode = isReducedMotionMode ? 'true' : 'false'
    },
    { immediate: true },
  )

  return {
    isInReducedMotionMode,
    setCustomPreferredMotion,
  }
})
export { useReducedMotionMode }
